import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { ApplicationException } from 'common/exceptions/application/ApplicationException';
import { Attachment } from 'common/models/Attachment';
import { CommunicationTemplate } from 'common/models/CommunicationTemplate';
import { MessageReferenceIds } from 'common/models/MessageReferenceIds';
import { SentimentPhraseDTO } from 'common/models/SentimentPhraseDTO';
import { ValidateFileRequest } from 'common/models/ValidateFileRequest';
import CONFIG from 'config';
import $ from 'jquery';
import { AddInFeatureNotSupportedException } from '../exceptions/application/AddInFeatureNotSupportedException';
import { Complaint } from '../models/Complaint';
import { ExternalConversationSource } from '../models/ExternalConversationSource';
import { Interaction } from '../models/Interaction';
import { OutlookAttachmentResponseDTO } from '../models/OutlookAttachmentResponseDTO';
import { SupportedOfficeItem } from '../utils/SupportedOfficeItem';
import * as Api from '../utils/api';
import { getDiffInHours, toIsoWithTimeZone } from '../utils/dateUtils';
import * as GraphApi from '../utils/graphApi';
import * as OutlookAsyncUtils from '../utils/outlookAsyncUtils';
import { getAsyncBody } from '../utils/outlookAsyncUtils';
import * as OutlookItemUtils from '../utils/outlookItemUtils';
import * as ProductAnalytics from '../utils/product-analytics';
import * as Ui from '../utils/uiUtils';
import { getStringValueOrNull } from '../utils/uiUtils';
import * as UserSettings from '../utils/user-settings';
import { Stakeholder } from './../models/Stakeholder';
import * as AppContacts from './contacts';
import * as ItemProperties from './item-properties';
import * as AppNewVersionSstkAutosave from './sstk-autosave';

const TAG = 'APS3: ';

const INLINE_IMAGE_SIZE_THRESHOLD = 1 * 1024 * 1024; // 1MB in bytes

// Ceiling on the combined size of the images left inside a saved body.
//
// INLINE_IMAGE_SIZE_THRESHOLD is per image, so it does nothing about a message carrying many
// smaller ones: every one of the 25 largest bodies saved after that check shipped came in under it,
// the biggest single image being 945 KB, yet the bodies ran to 8-62 MB (webapp#3267). Anything over
// this budget is uploaded as a normal attachment instead of being embedded.
//
// 512 KB is enough for an ordinary screenshot or a signature to stay inline and display exactly as
// before, and low enough that image-heavy mail stops producing multi-megabyte bodies.
const INLINE_IMAGE_TOTAL_BUDGET = 512 * 1024; // 512KB in bytes
const SIGNATURE_IMAGE_MAX_WIDTH_PX = 200;
const SIGNATURE_IMAGE_MAX_HEIGHT_PX = 100;

export async function saveMessage(item: SupportedOfficeItem) {
  console.log(TAG, new Date(), 'Start saveMessage');
  const checks = [ensureEmailSubjectIsNotExcluded(item)];
  // don't check appointments because they will be checked by UID while saving
  if (item.itemType !== 'appointment') {
    checks.push(ensureEmailIsNotDuplicate(item));
  }

  console.log(TAG, new Date(), 'Execute message checks');
  await Promise.all(checks);

  console.log(TAG, new Date(), 'Get shared properties');
  const sharedProperties = await OutlookAsyncUtils.getSharedProperties(item);

  console.log(TAG, new Date(), 'Get stakeholders from ui');
  const stakeholders = Ui.getStakeholdersFromUi();

  console.log(TAG, new Date(), 'Save stakeholders');
  const stakeholdersIds = await saveStakeholders(stakeholders);

  const emailType = $('#item-communication').val();

  let communication: Interaction | Complaint;
  let failedAttachmentNames: string[];
  if (emailType == 'Communication' || !emailType) {
    console.log(TAG, new Date(), 'Save interaction');
    ({ communication, failedAttachmentNames } = await saveInteraction(
      item,
      sharedProperties,
      stakeholdersIds
    ));
  } else {
    console.log(TAG, new Date(), 'Save complaint');
    ({ communication, failedAttachmentNames } = await saveComplaint(
      item,
      sharedProperties,
      stakeholdersIds
    ));

    $('#save-project').prop('selectedIndex', 0);
  }

  console.log(TAG, new Date(), 'Mark item as saved');
  await ItemProperties.markItemAsSavedThroughApi(item);

  console.log(TAG, new Date(), 'Track product analytics event');
  await ProductAnalytics.trackEvent({ eventName: 'used_email_plugin' });

  if (failedAttachmentNames.length > 0) {
    console.warn(TAG, 'Saved without attachments', failedAttachmentNames);
    Ui.showInfoDialog(
      `Your email was saved to ${
        CONFIG.appName
      }, but the following attachment(s) could not be saved and were not included: ${failedAttachmentNames.join(
        ', '
      )}.`
    );
  } else {
    Ui.showSaveSuccessfulDialog();
  }
  $('#selectBox').hide();

  if (UserSettings.getAutosave()) {
    console.log(TAG, new Date(), 'Get date from outlook item');
    const firstSaveDate = await OutlookItemUtils.getDateFromOutlookItem(item);

    console.log(TAG, new Date(), 'Save autosave contact');

    if (CONFIG.isNewSstkAutosaveContactEnabled) {
      AppNewVersionSstkAutosave.createOrUpdateContact(
        firstSaveDate,
        communication,
        stakeholders[0]
      );
    } else {
      saveAutosaveContact(
        firstSaveDate,
        communication,
        emailType,
        stakeholders[0]
      );
    }
  }

  console.log(TAG, new Date(), 'End saveMessage');
}

async function ensureEmailIsNotDuplicate(item: SupportedOfficeItem) {
  if (CONFIG.allowToSaveMessageAgain) return;

  const response = await Api.findDuplicateEmail({
    Subject: await OutlookItemUtils.getSubjectFromOutlookItem(item),
    EmailAddresses: [
      item.from.emailAddress,
      ...item.to.map((to) => to.emailAddress),
    ],
    Message: await getAsyncBody(item.body),
    DateTime: toIsoWithTimeZone(
      await OutlookItemUtils.getDateFromOutlookItem(item)
    ),
  });

  if (response.IsMatchFound) {
    throw new ApplicationException(
      `Email was already saved in ${CONFIG.appName} as ${response.DocumentType}${response.DocumentID}`
    );
  }
}

async function ensureEmailSubjectIsNotExcluded(item: SupportedOfficeItem) {
  const isExcluded = await Api.ensureEmailSubjectIsNotExcluded({
    Subject: await OutlookItemUtils.getSubjectFromOutlookItem(item),
  });
  if (isExcluded) {
    throw new ApplicationException(
      'Email subject contains words that are in the exclusion list'
    );
  }
}

async function saveStakeholders(stakeholders: Stakeholder[]) {
  const stakeholdersIds = [];
  for (const stakeholder of stakeholders) {
    const stakeholderID = await createOrUpdateStakeholder(stakeholder);
    stakeholdersIds.push(stakeholderID);
  }

  return stakeholdersIds;
}

export async function saveInteraction(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  stakeholderIds: string[]
) {
  console.log(TAG, 'Saving interaction', sharedProperties);

  // first, check if attachments are available and saved
  // so function would throw 'attachments not saved' error
  const { attachmentIds, failedAttachmentNames } =
    await uploadAttachmentsIfSupported(item, sharedProperties);

  const interaction = await getInteractionFromUi(item, sharedProperties);

  const { messageBody, largeInlineAttachmentIds, failedInlineImageNames } =
    await uploadInlineAttachmentsForItem(
      interaction.Message,
      item,
      sharedProperties
    );

  interaction.Message = messageBody;

  if (CONFIG.isGraphApiEnabled && item.itemType === 'appointment') {
    const id = await OutlookItemUtils.getItemId(item, sharedProperties);

    const uids = await OutlookItemUtils.getAppointmentUids(
      id,
      sharedProperties
    );

    if (uids.itemType === 'seriesMaster') {
      throw new ApplicationException(
        'Saving series of events is not supported. Please select a single event in series.'
      );
    }

    console.log(TAG, 'Retrieved Appointment UID: ', uids.uid);
    interaction.MeetingUID = uids.uid;
    interaction.MeetingRecurrenceID = uids.recurrenceId;
  }

  await captureMessageReferenceIds(interaction, item, sharedProperties);

  const interactionId = await Api.saveInteraction(interaction);

  await Api.linkInteractionToStakeholders(interactionId, stakeholderIds);
  const allInteractionAttachmentIds = [
    ...attachmentIds,
    ...largeInlineAttachmentIds,
  ];
  if (allInteractionAttachmentIds.length > 0) {
    await Api.linkInteractionToAttachments(
      interactionId,
      allInteractionAttachmentIds
    );
  }

  await ProductAnalytics.trackEvent({
    eventName: 'new_interaction_saved_from_outlook',
  });

  await ProductAnalytics.trackEvent({
    eventName:
      item.itemType === Office.MailboxEnums.ItemType.Message
        ? 'outlook_message_saved_interaction'
        : 'outlook_event_saved_interaction',
  });

  return {
    communication: interaction,
    failedAttachmentNames: [
      ...failedAttachmentNames,
      ...failedInlineImageNames,
    ],
  };
}

/**
 * Attaches the identifiers that let this interaction be threaded exactly rather than by inferring a
 * conversation from its subject.
 *
 * Nothing here may prevent a save. These headers are a bonus signal; the interaction is the thing
 * the user asked for, and an interaction saved without them still threads by subject. So every
 * failure path is swallowed and logged.
 *
 * Two signals, captured independently because they are available to different users:
 *
 *  - `conversationId` comes from Office.js and works in **both** add-in variants, including the
 *    restricted one that has no Graph access at all. It is the only conversation signal some users
 *    can provide.
 *  - The Message-ID and References chain need Graph (`Mail.ReadWrite`, already held), so they are
 *    only available in the full variant.
 *
 * Messages only. An appointment already threads on its meeting occurrence id, which is a stronger
 * key than either of these, and Graph does not return message headers for events.
 */
async function captureMessageReferenceIds(
  interaction: Interaction,
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  if (item.itemType !== Office.MailboxEnums.ItemType.Message) {
    return;
  }

  try {
    applyConversationId(
      interaction,
      OutlookItemUtils.getConversationIdFromOutlookItem(item)
    );

    if (!CONFIG.isGraphApiEnabled) {
      return;
    }

    const itemId = await OutlookItemUtils.getItemId(item, sharedProperties);

    applyMessageReferenceIds(
      interaction,
      await OutlookItemUtils.getMessageReferenceIds(itemId, sharedProperties)
    );
  } catch (error) {
    console.log(
      TAG,
      'Could not capture message reference ids; saving without them',
      error
    );
  }
}

/**
 * {@link captureMessageReferenceIds} for autosave, which reads messages through Graph instead of
 * from the item open in Outlook. The same rule applies: nothing here may prevent a save.
 *
 * The ids come from the message autosave already fetched, so the fields must be selected there.
 * Graph's conversation id is already in the REST format every save path stores.
 */
export function captureMessageReferenceIdsForGraphMessage(
  interaction: Interaction,
  message: MicrosoftGraph.Message
) {
  try {
    applyConversationId(interaction, message.conversationId ?? null);

    applyMessageReferenceIds(
      interaction,
      GraphApi.getMessageReferenceIdsFromMessage(message)
    );
  } catch (error) {
    console.log(
      TAG,
      'Could not capture message reference ids; saving without them',
      error
    );
  }
}

function applyConversationId(
  interaction: Interaction,
  conversationId: string | null
) {
  if (conversationId) {
    // Id and source travel together - the server cannot match one without the other.
    interaction.ExternalConversationID = conversationId;
    interaction.ExternalConversationSource =
      ExternalConversationSource.OutlookConversation;
  }
}

function applyMessageReferenceIds(
  interaction: Interaction,
  { internetMessageId, references }: MessageReferenceIds
) {
  if (internetMessageId) {
    interaction.InternetMessageID = internetMessageId;
  }

  if (references.length > 0) {
    interaction.MessageReferences = references;
  }
}

export async function saveComplaint(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  stakeholderIds: string[]
) {
  console.log(TAG, 'Saving complaint', sharedProperties);

  // first, check if attachments are available and saved
  // so function would throw 'attachments not saved' error
  const { attachmentIds, failedAttachmentNames } =
    await uploadAttachmentsIfSupported(item, sharedProperties);

  const complaint = await getComplaintFromUi(item, sharedProperties);

  const {
    messageBody: complaintMessageBody,
    largeInlineAttachmentIds: complaintLargeInlineAttachmentIds,
    failedInlineImageNames: complaintFailedInlineImageNames,
  } = await uploadInlineAttachmentsForItem(
    complaint.Message,
    item,
    sharedProperties
  );

  complaint.Message = complaintMessageBody;

  const interactionId = await Api.saveComplaint(complaint);

  await Api.linkComplaintToStakeholders(interactionId, stakeholderIds);
  const allComplaintAttachmentIds = [
    ...attachmentIds,
    ...complaintLargeInlineAttachmentIds,
  ];
  if (allComplaintAttachmentIds.length > 0) {
    await Api.linkComplaintToAttachments(
      interactionId,
      allComplaintAttachmentIds
    );
  }

  await ProductAnalytics.trackEvent({
    eventName: 'new_complaint_saved_from_outlook',
  });

  return {
    communication: complaint,
    failedAttachmentNames: [
      ...failedAttachmentNames,
      ...complaintFailedInlineImageNames,
    ],
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function saveAutosaveContact(
  firstSaveDate: Date,
  communication: Interaction | Complaint,
  emailType: string | number | string[],
  stakeholder: Stakeholder
) {
  await AppContacts.createAutosaveContact(
    UserSettings.getProject(),
    emailType as string,
    stakeholder,
    communication as CommunicationTemplate,
    firstSaveDate
  );
}

async function getInteractionFromUi(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  const subject = await OutlookItemUtils.getSubjectFromOutlookItem(item);
  const location = await OutlookItemUtils.getLocationFromOutlookItem(item);
  const direction = await OutlookItemUtils.getDirectionFromOutlookItem(
    item,
    sharedProperties
  );
  const dateTime = await OutlookItemUtils.getDateFromOutlookItem(item);
  const endDateTime = await OutlookItemUtils.getEndDateFromOutlookItem(item);
  const duration = getDiffInHours(endDateTime, dateTime);
  const body = await getAsyncBody(item.body);
  const confidentialValue = $('#item-confidential').val();

  let confidential = null;
  if (CONFIG.isConfidentialOptionsEnabled && confidentialValue) {
    confidential = { Value: confidentialValue };
  }

  let interactionType = null;
  const interactionTypeID = parseInt(
    getStringValueOrNull('#item-interaction-type')
  );
  if (interactionTypeID > 0) {
    interactionType = { Value: interactionTypeID };
  }

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const interaction: Interaction = {
    Subject: subject,
    CommunicationDate: toIsoWithTimeZone(dateTime),
    Method: {
      Text: OutlookItemUtils.getInteractionMethodFromOutlookItem(item),
    },
    Direction: communicationDirection,
    Location: location,
    Message: body,
    Confidential: confidential || null,
    Duration: duration,
    InteractionType: interactionType,
    SentimentPhrases: $('#item-tag')
      .select2('data')
      ?.map((i) => {
        return {
          SentimentPhraseID: i.id,
          Phrase: i.text,
        } as SentimentPhraseDTO;
      }),
    AdditionalSharedProjectIDs: $('#save-project')
      .select2('data')
      ?.map((i) => i.id),
  };
  return interaction;
}

async function getComplaintFromUi(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  const subject = await OutlookItemUtils.getSubjectFromOutlookItem(item);
  const direction = await OutlookItemUtils.getDirectionFromOutlookItem(
    item,
    sharedProperties
  );
  const dateTime = await OutlookItemUtils.getDateFromOutlookItem(item);
  const body = await getAsyncBody(item.body);

  const communicationDirection = await Api.getDirectionFromServer(direction);

  const confidentialValue = $('#item-confidential').val();
  const priorityValue = $('#complaint-priorities').val()
    ? $('#complaint-priorities').val()
    : '';
  const assignedTo = $('#item-assigned-to').val()
    ? $('#item-assigned-to').val()
    : '';
  const statusValue = $('#item-complaint-status').val()
    ? $('#item-complaint-status').val()
    : '';

  let confidential = null;
  if (CONFIG.isConfidentialOptionsEnabled && confidentialValue) {
    confidential = { Value: confidentialValue };
  }
  let priority = null;
  if (priorityValue) {
    priority = { Value: priorityValue.toString() };
  }
  let status = null;
  if (statusValue) {
    status = { Value: statusValue.toString() };
  }

  const complaint: Complaint = {
    Subject: subject,
    AssignedTo: {
      Value: assignedTo.toString(),
    },
    DateOpened: toIsoWithTimeZone(dateTime),
    Method: {
      Text: OutlookItemUtils.getInteractionMethodFromOutlookItem(item),
    },
    Direction: communicationDirection,
    Message: body,
    Priority: priority,
    Confidential: confidential,
    Status: status,
    InteractionType: null,
    SentimentPhrases: null,
  };

  return complaint;
}

interface UploadAttachmentsResult {
  attachmentIds: string[];
  /** Attachments that could not be retrieved from Outlook, even through Graph. */
  failedAttachmentNames: string[];
}

async function uploadAttachmentsIfSupported(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<UploadAttachmentsResult> {
  try {
    return await uploadAttachmentsForItem(item, sharedProperties);
  } catch (error) {
    if (error instanceof AddInFeatureNotSupportedException) {
      return { attachmentIds: [], failedAttachmentNames: [] };
    } else {
      throw error;
    }
  }
}

export async function uploadAttachmentsForItem(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<UploadAttachmentsResult> {
  if (!CONFIG.isAttachmentsEnabled) {
    console.log(TAG, 'Attachments are not enabled, skipping');
    return { attachmentIds: [], failedAttachmentNames: [] };
  } else if (!OutlookItemUtils.canSaveAttachments()) {
    console.log(TAG, 'Attachments are not supported, skipping');
    return { attachmentIds: [], failedAttachmentNames: [] };
  } else {
    console.log(TAG, 'Uploading attachments');
  }

  const { attachments, failedAttachmentNames } =
    await OutlookItemUtils.getSupportedAttachmentsForItem(
      item,
      sharedProperties
    );

  const attachmentIds = await uploadAttachmentContent(attachments);

  return { attachmentIds, failedAttachmentNames };
}

export async function uploadAttachmentsForItemId(
  itemId: string,
  itemType: string,
  sharedProperties: Office.SharedProperties | undefined
) {
  if (!CONFIG.isAttachmentsEnabled) {
    console.log(TAG, 'Attachments are not enabled, skipping');
    return [];
  } else {
    console.log(TAG, 'Uploading attachments');
  }

  const attachments = await OutlookItemUtils.getSupportedAttachmentsForItemId(
    itemId,
    itemType,
    sharedProperties
  );

  return await uploadAttachmentContent(attachments);
}

export async function uploadInlineAttachmentsForItem(
  body: string,
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<{
  messageBody: string;
  largeInlineAttachmentIds: string[];
  failedInlineImageNames: string[];
}> {
  const { attachments } = await OutlookItemUtils.getSupportedAttachmentsForItem(
    item,
    sharedProperties,
    true
  );

  const largeInlineAttachments = attachments.filter(
    (a) => a.isInline && a.contentId && a.size > INLINE_IMAGE_SIZE_THRESHOLD
  );

  const largeUpload = await uploadLargeInlineAttachments(
    largeInlineAttachments
  );

  const bodyWithLargeImagesStripped =
    OutlookAsyncUtils.stripInlineAttachmentCidReferences(
      body,
      largeInlineAttachments
    );

  const remainingAfterLarge = attachments.filter(
    (a) => !largeInlineAttachments.includes(a)
  );

  let messageBody = await OutlookAsyncUtils.updateEmailWithInlineAttachments(
    bodyWithLargeImagesStripped,
    remainingAfterLarge,
    SIGNATURE_IMAGE_MAX_WIDTH_PX,
    SIGNATURE_IMAGE_MAX_HEIGHT_PX
  );

  const { processedHtml, extractedImages } =
    OutlookAsyncUtils.extractOversizedBase64Images(
      messageBody,
      INLINE_IMAGE_SIZE_THRESHOLD,
      INLINE_IMAGE_TOTAL_BUDGET
    );
  messageBody = processedHtml;

  const base64Upload = await uploadLargeInlineAttachments(extractedImages);

  return {
    messageBody,
    largeInlineAttachmentIds: [...largeUpload.ids, ...base64Upload.ids],
    failedInlineImageNames: [
      ...largeUpload.failedNames,
      ...base64Upload.failedNames,
    ],
  };
}

export async function updateInlineAttachments(
  body: string,
  itemId: string,
  itemType: string,
  sharedProperties: Office.SharedProperties | undefined
): Promise<{ messageBody: string; largeInlineAttachmentIds: string[] }> {
  const attachments = await OutlookItemUtils.getSupportedAttachmentsForItemId(
    itemId,
    itemType,
    sharedProperties,
    true
  );

  const largeInlineAttachments = attachments.filter(
    (a) => a.isInline && a.contentId && a.size > INLINE_IMAGE_SIZE_THRESHOLD
  );

  const largeUpload = await uploadLargeInlineAttachments(
    largeInlineAttachments
  );

  const bodyWithLargeImagesStripped =
    OutlookAsyncUtils.stripInlineAttachmentCidReferences(
      body,
      largeInlineAttachments
    );

  const remainingAfterLarge = attachments.filter(
    (a) => !largeInlineAttachments.includes(a)
  );

  let messageBody = await OutlookAsyncUtils.updateEmailWithInlineAttachments(
    bodyWithLargeImagesStripped,
    remainingAfterLarge,
    SIGNATURE_IMAGE_MAX_WIDTH_PX,
    SIGNATURE_IMAGE_MAX_HEIGHT_PX
  );

  const { processedHtml, extractedImages } =
    OutlookAsyncUtils.extractOversizedBase64Images(
      messageBody,
      INLINE_IMAGE_SIZE_THRESHOLD,
      INLINE_IMAGE_TOTAL_BUDGET
    );
  messageBody = processedHtml;

  const base64Upload = await uploadLargeInlineAttachments(extractedImages);

  return {
    messageBody,
    largeInlineAttachmentIds: [...largeUpload.ids, ...base64Upload.ids],
  };
}

async function uploadLargeInlineAttachments(
  attachments: Attachment[]
): Promise<{ ids: string[]; failedNames: string[] }> {
  if (attachments.length === 0) {
    return { ids: [], failedNames: [] };
  }

  console.log(
    TAG,
    `Uploading ${attachments.length} large inline image(s) exceeding ${
      INLINE_IMAGE_SIZE_THRESHOLD / (1024 * 1024)
    }MB as file attachments`
  );

  const maxRequestSize = 50 * 1024 * 1024;
  const responsesByAttachment: OutlookAttachmentResponseDTO[] = [];
  const failedNames: string[] = [];
  let files: Attachment[] = [];

  // A rejected image costs the user that image, not the whole save. The message body is saved
  // either way; the image is listed in the dialog as not included.
  const uploadBatch = async (batch: Attachment[]) => {
    try {
      const response = await Api.uploadOutlookAttachments({
        attachments: batch,
      });
      responsesByAttachment.push(...response.ResponsesByAttachment);
    } catch (error) {
      console.warn(TAG, 'Failed to upload inline image(s)', error);
      failedNames.push(...batch.map((a) => a.name));
    }
  };

  // Mark as non-inline so the server creates a proper linkable file entity
  const attachmentsAsFiles = attachments.map((a) => ({
    ...a,
    isInline: false,
  }));

  for (const attachment of attachmentsAsFiles) {
    const totalSize = files.reduce((a, b) => +a + +b.contentBytes?.length, 0);
    if (totalSize + attachment.contentBytes?.length > maxRequestSize) {
      await uploadBatch(files);
      files = [];
    }
    files.push(attachment);
  }

  if (files.length > 0) {
    await uploadBatch(files);
  }

  return {
    ids: responsesByAttachment.filter((a) => a.IsSuccess).map((a) => a.FileID),
    failedNames,
  };
}

async function uploadAttachmentContent(attachments: Attachment[]) {
  if (attachments.length === 0) {
    return [];
  }

  for (const attachment of [...attachments]) {
    const attachmentSize = attachment.size > 0 ? attachment.size / 1024 : 0;
    const request: ValidateFileRequest = {
      FileName: attachment.name,
      SizeKb: attachmentSize,
    };

    console.log(
      TAG,
      'Validating attachment with Darzin API',
      attachment.name,
      attachment.size
    );

    const isValid = await Api.validateAttachment(request);
    if (!isValid) {
      const index = attachments.indexOf(attachment);
      attachments.splice(index, 1);
      console.log(TAG, 'Attachment is invalid');
    } else {
      console.log(TAG, 'Attachment is valid');
    }
  }

  const maxRequestSize = 50 * 1024 * 1024; // API max request size is 50MB
  const responsesByAttachment: OutlookAttachmentResponseDTO[] = [];
  let files: Attachment[] = [];
  for (const attachment of attachments.filter((a) => !a.isInline)) {
    const totalSize = files.reduce((a, b) => +a + +b.contentBytes?.length, 0);

    if (totalSize + attachment.contentBytes?.length > maxRequestSize) {
      const response = await Api.uploadOutlookAttachments({
        attachments: files,
      });
      responsesByAttachment.push(...response.ResponsesByAttachment);
      files = [];
    }
    files.push(attachment);
  }

  if (files.length > 0) {
    const response = await Api.uploadOutlookAttachments({
      attachments: files,
    });
    responsesByAttachment.push(...response.ResponsesByAttachment);
  }

  const attachmentIds = responsesByAttachment
    .filter((a) => a.IsSuccess)
    .map((a) => a.FileID);

  console.log(TAG, 'Uploaded attachments');
  return attachmentIds;
}

export async function createOrUpdateStakeholder(stakeholder: Stakeholder) {
  let stakeholderId = await Api.findStakeholder(stakeholder);

  // if stakeholder already exists, assign his ID to request
  // so stakeholder with this ID will be updated
  if (stakeholderId != '0') {
    // eslint-disable-next-line require-atomic-updates
    stakeholder.ID = stakeholderId;
    await Api.mergeStakeholderInfo(stakeholder);
  } else {
    stakeholderId = await Api.saveStakeholder(stakeholder);
  }

  return stakeholderId;
}
